import { useEffect, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import {
  EmailField,
  FormFieldset,
  ItemsField,
  SelectField,
  SimpleHTMLField,
  ToggleField,
  TextField,
} from '@Forms/fields.jsx';
import { slugifyDash, urlFormat, slugifyOnBlur } from '@Forms/formatters.js';

import { validateEmail } from '@Forms/validators.js';
import { errorsFromMutate } from '@Forms/utils.js';
import {
  ButtonSpinnerIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
} from '@svgs';

const ContactEmailItem = ({
  field,
  fieldPrefix,
  index,
  itemsArray,
  register,
}) => {
  // custom component for the ItemsField implementation
  return (
    <div className="sb-flex">
      <input
        type="email"
        {...register(`${fieldPrefix}`, {
          required: 'This field is required',
          validate: validateEmail,
        })}
      />
      <button
        type="button"
        className="sb-btn"
        disabled={index === 0}
        onClick={() => itemsArray.move(index, index - 1)}
      >
        <ChevronUpIcon />
      </button>
      <button
        type="button"
        className="sb-btn"
        disabled={index === itemsArray.fields.length - 1}
        onClick={() => itemsArray.move(index, index + 1)}
      >
        <ChevronDownIcon />
      </button>
      <button
        type="button"
        className="sb-btn"
        onClick={() => {
          if (window.confirm('Are you sure to delete this record?')) {
            itemsArray.remove(index);
          }
        }}
      >
        <TrashIcon />
      </button>
    </div>
  );
};

const PhoneItem = ({ field, fieldPrefix, index, itemsArray, register }) => {
  // custom component for the ItemsField implementation
  return (
    <div className="sb-flex">
      <input
        type="text"
        {...register(`${fieldPrefix}.number`, {
          required: 'This field is required',
        })}
      />
      <label>Type</label>
      <select
        {...register(`${fieldPrefix}.type`, {
          required: 'This field is required',
        })}
      >
        <option value="">Select type...</option>
        <option value="phone">Primary phone</option>
        <option value="secondary">Secondary phone</option>
        <option value="third">Third phone</option>
        <option value="fax">Fax number</option>
        <option value="mobile">Mobile number</option>
        <option value="tollfree">Toll-free number</option>
      </select>
      <button
        type="button"
        className="sb-btn"
        onClick={() => {
          if (window.confirm('Are you sure to delete this record?')) {
            itemsArray.remove(index);
          }
        }}
      >
        <TrashIcon />
      </button>
    </div>
  );
};

const ServiceTagItem = ({
  field,
  fieldPrefix,
  index,
  itemsArray,
  register,
}) => {
  // custom component for the ItemsField implementation
  return (
    <div className="sb-flex">
      <input
        type="text"
        {...register(`${fieldPrefix}`, {
          required: 'This field is required',
          onChange: slugifyDash,
          onBlur: slugifyOnBlur,
        })}
      />
      <button
        type="button"
        className="sb-btn"
        onClick={() => {
          if (window.confirm('Are you sure to delete this record?')) {
            itemsArray.remove(index);
          }
        }}
      >
        <TrashIcon />
      </button>
    </div>
  );
};

export const LocationForm = ({
  disabled,
  mutation,
  constantsData,
  locationData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
    setValue,
    control,
    reset,
    resetField,
    trigger,
    watch,
  } = useForm({
    defaultValues: locationData,
    mode: 'onBlur',
  });
  const { isValid } = useFormState({ control });

  // handling for tinymce fields
  const editorRefs = useRef({});

  const onSubmit = (data) => {
    // remove read_only_id from data;
    delete data.read_only_id;

    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  const showField = (field_name) => {
    // util for future hidding of form fields
    return true;
  };
  const showFieldGroup = (field_group) => {
    // util for future hidding of form fields
    return true;
  };

  // state used based on the currently selected service_area_only
  const [serviceAreaOnly, setServiceAreaOnly] = useState(false);
  useEffect(() => {
    // set the serviceAreaOnly based on the current type
    setServiceAreaOnly(getValues(`service_area_only`));
  });

  // change event on service area toggle
  const serviceAreaOnlyChange = (e) => {
    setServiceAreaOnly(e.target.value);
    reset({
      street: null,
      street2: null,
    });
  };

  // state used based on the currently selected automatic_latlon
  const [automaticLatLon, setAutomaticLatLon] = useState(false);
  useEffect(() => {
    // set the automaticLatLon based on the current type
    setAutomaticLatLon(getValues(`automatic_latlon`));
  });
  // change event on automatic_latlon toggle
  const automaticLatLonChange = (e) => {
    setAutomaticLatLon(e.target.value);
  };

  // state used based on the currently selected automatic_timezone
  const [automaticTimezone, setAutomaticTimezone] = useState(false);
  useEffect(() => {
    // set the automaticTimezone based on the current type
    setAutomaticTimezone(getValues(`automatic_timezone`));
  });
  // change event on automatic_timezone toggle
  const automaticTimezoneChange = (e) => {
    setAutomaticTimezone(e.target.value);
  };

  // state used based on the currently selected value of the country
  const [country, setCountry] = useState(null);
  useEffect(() => {
    // set the country based on the current type
    setCountry(getValues(`country`));
  });

  // change event on country
  const countryChange = (e) => {
    setCountry(e.target.value);

    if (!constantsData.states_provinces[e.target.value]) {
      // if country does not have states, set state to null
      reset({
        [`state`]: null,
      });
    }
  };

  // country dropdown choices
  let countryChoices = constantsData.countries.map((d) => {
    return (
      <option key={d.country_code} value={d.country_code}>
        {d.country}
      </option>
    );
  });

  // state dropdown choices, based on current country
  let stateChoices =
    country && constantsData.states_provinces[country]
      ? constantsData.states_provinces[country].map((d) => {
          return (
            <option key={d.state_code} value={d.state_code}>
              {d.state}
            </option>
          );
        })
      : [];

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` +
        (mutation.isPending || disabled ? 'sb-form-disabled' : '')
      }
    >
      {showField('location_name') && (
        <FormFieldset>
          <TextField
            register={register}
            error={errors.location_name}
            options={{ required: 'This field is required' }}
            name="location_name"
            label="Location name"
            helpText={`Name for the location. Typically including the primary brand as well as the associated geography/territory - Example: "Your Brand Phoenix"`}
          />
        </FormFieldset>
      )}
      {showField('location_display_name') && (
        <FormFieldset>
          <TextField
            register={register}
            error={errors.location_display_name}
            name="location_display_name"
            label="Location display name"
            helpText={`Name of a Location without the primary brand. Example: "Phoenix". Typically used within the location finder or website`}
          />
        </FormFieldset>
      )}
      {showFieldGroup('contactDetails') && (
        <FormFieldset legend="Contact details">
          <ItemsField
            ItemComponent={ContactEmailItem}
            defaultItemData={''}
            register={register}
            error={errors.contact_emails}
            control={control}
            setValue={setValue}
            name="contact_emails"
            label="Contact emails"
            helpText="List of contact emails used for Form lead delivery"
          />
          <ItemsField
            ItemComponent={PhoneItem}
            defaultItemData={''}
            register={register}
            error={errors.phones}
            control={control}
            setValue={setValue}
            name="phones"
            label="Phone numbers"
            helpText="Contact phone numbers for the location by type"
          />
        </FormFieldset>
      )}
      {showFieldGroup('locationUrls') && (
        <FormFieldset legend="Locations URLs and linking">
          <TextField
            register={register}
            error={errors.facebook_url}
            options={{ onChange: urlFormat }}
            name="location_url"
            label="Location website or page"
            placeholder={`https://www.brandname.com/location`}
            helpText="Local website or Location page URL - used within the Project to link to the Location"
          />
          <TextField
            register={register}
            error={errors.homepage_url}
            options={{ onChange: urlFormat }}
            name="homepage_url"
            label="External URL"
            placeholder={`https://www.locationname.com`}
            helpText="If the website for this Location is hosted outside DevHub, its URL is set here"
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.published}
            name="published"
            label="Published"
            helpText="Location is published and should be available within the Location finder and other website experiences"
          />
        </FormFieldset>
      )}
      {showFieldGroup('address') && (
        <FormFieldset legend="Address">
          {!serviceAreaOnly && (
            <>
              <TextField
                register={register}
                error={errors.street}
                name="street"
                label="Street"
              />
              <TextField
                register={register}
                error={errors.street2}
                name="street2"
                label="Apt / Unit"
              />
            </>
          )}
          <TextField
            register={register}
            error={errors.city}
            options={{ required: 'This field is required' }}
            name="city"
            label="City"
          />
          <SelectField
            register={register}
            error={errors.country}
            options={{
              required: 'This field is required',
              onChange: countryChange,
            }}
            name="country"
            label="Country"
          >
            <option value="">Choose a country...</option>
            {countryChoices}
          </SelectField>
          {country && stateChoices.length > 0 && (
            <SelectField
              register={register}
              error={errors.state}
              name="state"
              label="State/Province"
            >
              <option value="">Choose a state...</option>
              {stateChoices}
            </SelectField>
          )}
          <TextField
            register={register}
            error={errors.postal_code}
            name="postal_code"
            label="Postal code"
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.service_area_only}
            options={{ onChange: serviceAreaOnlyChange }}
            name="service_area_only"
            label="Service area only location"
            helpText="Location that operates in service areas to serve it's customers directly, but doesn't serve customers at a business address. Street address will be hidden from customers"
          />
        </FormFieldset>
      )}
      {showFieldGroup('geoCoordinates') && (
        <FormFieldset legend="Geo-coordinates and timezone">
          <ToggleField
            register={register}
            control={control}
            error={errors.automatic_latlon}
            options={{ onChange: automaticLatLonChange }}
            name="automatic_latlon"
            label="Automaticly determine geo-coordinates"
            helpText="Geocode the Locations address to a latitude/longitude point. Disable to manually set a latitude/longitude below"
          />
          <TextField
            register={register}
            error={errors.lat}
            name="lat"
            label="Latitude"
            placeholder={`44.5555`}
            disabled={automaticLatLon}
          />
          <TextField
            register={register}
            error={errors.lon}
            name="lon"
            label="Longitude"
            placeholder={`-122.633`}
            disabled={automaticLatLon}
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.automatic_timezone}
            options={{ onChange: automaticTimezoneChange }}
            name="automatic_timezone"
            label="Automaticly determine timezone"
            helpText="Auto assign timezone using location latitude and longitude. Disable to manually set a timezone below"
          />
          <TextField
            register={register}
            error={errors.timezone}
            name="timezone"
            label="Timezone"
            placeholder={`America/Los_Angeles`}
            helpText="Entered using TZ database name format (i.e. America/Los_Angeles)"
            disabled={automaticTimezone}
          />
        </FormFieldset>
      )}
      {showFieldGroup('serviceTags') && (
        <FormFieldset legend="Filtering properties">
          <ItemsField
            ItemComponent={ServiceTagItem}
            defaultItemData={''}
            register={register}
            error={errors.services_tags}
            control={control}
            setValue={setValue}
            name="services_tags"
            label="Service tags"
            helpText="Tags for location for use in filtering (location finder, etc)"
          />
        </FormFieldset>
      )}
      <FormFieldset legend="Description">
        <SimpleHTMLField
          register={register}
          control={control}
          editorRefs={editorRefs}
          initialValue={locationData.description}
          error={errors.description}
          name="description"
          label="Description"
        />
      </FormFieldset>
      <FormFieldset legend="Location ID">
        <TextField
          register={register}
          name="read_only_id"
          label="Location ID"
          readOnly={true}
          helpText="Read only"
          value={locationData.id}
        />
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {/*<button className="sb-btn-cancel sb-btn" onClick={resetForm}>Cancel</button>*/}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={mutation.isPending || disabled}
          >
            {mutation.isPending ? 'Publishing...' : 'Publish changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error publishing changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully published" />
        )}
      </div>
    </form>
  );
};
